/**
 * View-only "ghost" entity utilities.
 *
 * A ghost is a clone of an entity that is added directly to the render view
 * (via view.add()) rather than to the database — used for fast interactive
 * preview (drag, resize, copy placement) without recordGraphicsModified()
 * overhead per frame. See BaseHandle for the original pattern this was
 * extracted from.
 */

import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import { deleteObj } from "../core/delete";
import { openAs } from "../core/oda";

/**
 * Create a ghost entity for view-based rendering.
 *
 * Unlike the database-based approach, this creates a clone that is NOT
 * added to the database. It will be added directly to the view for
 * fast interactive rendering (caller is responsible for `view.add(ghost, null)`
 * and, once done, `view.erase(ghost)` followed by a deferred `deleteObj(ghost)`
 * — see the caller-side gotcha note below).
 *
 * @returns The cloned entity (not an ID since it's not in database)
 */
export function createViewGhost(
  module: DrawingModuleType,
  viewer: DRDrawingViewer,
  entityId: DrawingModule.OdDbObjectId,
): DrawingModule.OdDbEntity | undefined {
  let entity: DrawingModule.OdDbEntity | null = null;
  let clonePtr: DrawingModule.OdRxObjectPtr | null = null;
  try {
    // Open the original entity for reading
    entity = openAs(entityId, module.OdDbEntity, 0);

    // Clone the entity
    clonePtr = entity.clone();
    const cloneRaw = clonePtr.get();
    const clone = module.OdDbEntity.cast(cloneRaw);

    if (!clone) {
      console.warn("[ghost] Could not cast cloned entity for view ghost");
      return undefined;
    }

    // Set database defaults so the clone renders properly
    const db = viewer.database;
    if (db) {
      clone.setDatabaseDefaults(db, true);
    }

    // clonePtr is released by the finally below, NOT abandoned: cast() returns
    // an OdSmartPtr that holds its own reference to the same C++ object, so the
    // clone stays alive on the caller's single reference and dies on their
    // deleteObj(). Nulling clonePtr out here — which this used to do — left
    // its reference dangling forever and leaked one entity per ghost, i.e. per
    // drag. Same split ownership as openAs(), which frees the open pointer and
    // returns the cast.
    return clone;
  } finally {
    if (clonePtr) deleteObj(clonePtr);
    if (entity) deleteObj(entity);
  }
}
